import {
	Component,
	ComponentInterface,
	Element,
	h,
	Host,
	Method,
	State,
	Prop,
	Watch,
	Listen,
} from '@stencil/core';
import { isIe } from '../../../common/_helpers/browsers';
import { RevealAnimationStates } from '../_types/animations';
import { FundCardLayoutMode } from '../_types/fund-card';
import { FundCardInterface } from '../_interfaces/fund-card/fund-card';
import { getChildComponent } from '../_helpers/prefixes';

@Component({
	tag: 'ds-fund-card',
})
export class FundCard implements ComponentInterface, FundCardInterface {
	// NOTE: One not immediately obvious purpose of this web component is to ensure there is a good fallback solution for
	// IE11 for the Tooltip web components inside it. See the SCSS file for details (it looks for the presence of the CSS
	// class 'hydrated' which is added when the web component is rendered.

	@Element() el: HTMLDsFundCardElement;
	esgData: HTMLDivElement;
	ticker: HTMLSpanElement;
	tickerText = '';
	isIE = isIe();
	@State() layout: FundCardLayoutMode = FundCardLayoutMode.Standard;
	hideEsgDataTimout: ReturnType<typeof setTimeout>;
	@Prop() revealAnimation: RevealAnimationStates = 'on';
	@Prop({ reflect: true }) animateHover = !this.isIE;
	@Prop() index: number;

	componentWillLoad(): void {
		this.esgData = this.el.querySelector('.ds-esg-data');

		// Hide ESG data by default
		if (this.esgData) {
			this.esgData.style.display = 'none';
		}
		this.setAriaLabels();
		this.setCompareButtonAriaLabels();
	}

	componentDidLoad(): void {
		if (!this.isIE) {
			this.setHoverAnimation();
		}
	}

	@Watch('animateHover')
	onAnimateHoverChange(newState: boolean, prevState: boolean) {
		if (newState !== prevState && !this.isIE) {
			this.setHoverAnimation();
		}
	}

	@Listen('tooltipOpen')
	onTooltipOpen() {
		this.removeAnimations();
	}

	@Listen('tooltipClosed')
	onTooltipClose() {
		this.setHoverAnimation();
	}

	@Listen('tabSelected', { target: 'body' })
	onTabSwitch() {
		if (!this.el.classList.contains('ds-animate-reveal')) {
			this.el.classList.add('ds-animate-reveal');
		}
	}

	@Method()
	async toggleEsgData(on: boolean): Promise<void> {
		if (on) {
			// Clear any existing timeout that would hide the ESG data after 0.5 (to cover if the switch is pressed twice in quick succession)
			if (this.hideEsgDataTimout) {
				clearTimeout(this.hideEsgDataTimout);
			}

			// Ensure ESG data is displaid, even if at first it will have a 0px height.
			this.esgData.style.display = 'flex';

			// Changing the layout will kick off the CSS height transition.
			this.layout = FundCardLayoutMode.ESG;
		} else {
			// CSS animate height back to 0
			this.layout = FundCardLayoutMode.Standard;

			// Wait 0.5s for CSS animation to complete, then hide ESG data to prevent tooltips being focusable
			this.hideEsgDataTimout = setTimeout(
				() => (this.esgData.style.display = 'none'),
				500
			);
		}
	}

	setHoverAnimation() {
		const animationClass = 'ds-animate-hover';
		const cardEl = getChildComponent(this.el, 'ds-card');
		if (cardEl) {
			if (this.animateHover) {
				cardEl.classList.add(animationClass);
			} else {
				cardEl.classList.remove(animationClass);
			}
		}
	}

	removeAnimations() {
		const hoverAnimationClass = 'ds-animate-hover';
		const revealAnimationClass = 'ds-animate-reveal';
		const cardEl = getChildComponent(this.el, 'ds-card');
		if (cardEl && this.animateHover) {
			cardEl.classList.remove(hoverAnimationClass);
			this.el.classList.remove(revealAnimationClass);
		}
	}

	// Adds dots to acronyms so that they are read out as letters by a screenreader instead of making them into strange words
	addDotsToAcronym(acronymString: string): string {
		if (acronymString) {
			return Array.from(acronymString)
				.map((letter) => letter + '.')
				.join('');
		}
	}

	setAriaLabels(): void {
		this.ticker = this.el.querySelector('span.ds-ticker');

		if (this.ticker) {
			this.tickerText = this.addDotsToAcronym(this.ticker.textContent);
			this.ticker.setAttribute('aria-label', `Ticker ${this.tickerText}`);
		}
	}

	setCompareButtonAriaLabels(): void {
		const viewFundButtonEl: HTMLDsButtonElement = this.el.querySelector(
			'.ds-view-fund-button'
		);
		const fundNameEl = this.el.querySelector('span.ds-heading-s-lc');

		if (viewFundButtonEl && fundNameEl) {
			viewFundButtonEl.accessibilityLabel += ` ${fundNameEl.textContent} ${this.tickerText}`;
		}
	}

	render() {
		return (
			<Host
				class={{
					[`ds-layout-${this.layout}`]: Boolean(this.layout),
					'ds-animate-reveal': !this.isIE,
					[`ds-animate-reveal--delay-${
						this.index + 1 > 12 ? 12 * 20 : this.index * 20
					}`]:
						!this.isIE &&
						this.revealAnimation === 'delayed' &&
						typeof this.index === 'number' &&
						this.index > 0,
				}}
			>
				<slot></slot>
			</Host>
		);
	}
}
